/**
 * Localized Support ticket event labels from canonical event_type + payload.
 * Never returns raw technical keys to end users.
 */

const STATUS_KEYS = {
  open: 'support:status.open',
  waiting_for_support: 'support:status.waiting_for_support',
  waiting_for_customer: 'support:status.waiting_for_customer',
  resolved: 'support:status.resolved',
  closed: 'support:status.closed',
};

const PRIORITY_KEYS = {
  low: 'support:priority.low',
  normal: 'support:priority.normal',
  high: 'support:priority.high',
  urgent: 'support:priority.urgent',
  blocker: 'support:priority.urgent',
};

function translateStatus(t, code) {
  if (!code) return '';
  const key = STATUS_KEYS[code];
  return key ? t(key, code) : String(code);
}

function translatePriority(t, code) {
  if (!code) return '';
  const key = PRIORITY_KEYS[code];
  return key ? t(key, code) : String(code);
}

function assigneeName(payload = {}) {
  return (
    payload.assignee_name
    || payload.assigned_to_name
    || payload.user_name
    || payload.name
    || null
  );
}

/**
 * @param {object} event
 * @param {Function} t i18next t bound with support/common namespaces
 * @returns {{ label: string, isKnown: boolean }}
 */
export function formatSupportEventLabel(event, t) {
  const type = String(event?.event_type || '');
  const payload = event?.payload && typeof event.payload === 'object' ? event.payload : {};

  switch (type) {
    case 'ticket.created':
      return { label: t('support:event.ticket_created'), isKnown: true };

    case 'ticket.status_changed': {
      const from = translateStatus(t, payload.from ?? payload.from_status ?? payload.old);
      const to = translateStatus(t, payload.to ?? payload.to_status ?? payload.new ?? payload.status);
      if (from && to) {
        return {
          label: t('support:event.status_changed_from_to', { from, to }),
          isKnown: true,
        };
      }
      if (to) {
        return {
          label: t('support:event.status_changed_to', { to }),
          isKnown: true,
        };
      }
      return { label: t('support:event.status_changed'), isKnown: true };
    }

    case 'ticket.priority_changed': {
      const from = translatePriority(t, payload.from ?? payload.from_priority ?? payload.old);
      const to = translatePriority(t, payload.to ?? payload.to_priority ?? payload.new ?? payload.priority);
      if (from && to) {
        return {
          label: t('support:event.priority_changed_from_to', { from, to }),
          isKnown: true,
        };
      }
      if (to) {
        return {
          label: t('support:event.priority_changed_to', { to }),
          isKnown: true,
        };
      }
      return { label: t('support:event.priority_changed'), isKnown: true };
    }

    case 'ticket.assigned': {
      const name = assigneeName(payload);
      const assigneeId = payload.assigned_to_user_id ?? payload.assignee_id;
      if (assigneeId == null || assigneeId === '') {
        return { label: t('support:event.ticket_unassigned'), isKnown: true };
      }
      if (name) {
        return {
          label: t('support:event.ticket_assigned_to', { name }),
          isKnown: true,
        };
      }
      return { label: t('support:event.ticket_assigned'), isKnown: true };
    }

    case 'ticket.unassigned':
      return { label: t('support:event.ticket_unassigned'), isKnown: true };

    case 'ticket.message_added':
    case 'message.created':
      return { label: t('support:event.message_added'), isKnown: true };

    case 'ticket.internal_note_added':
    case 'internal_note.created':
      return { label: t('support:event.internal_note_added'), isKnown: true };

    case 'attachment_uploaded':
    case 'attachment.added':
      return { label: t('support:event.attachment_uploaded'), isKnown: true };

    case 'attachment_deleted':
    case 'attachment.deleted':
      return { label: t('support:event.attachment_deleted'), isKnown: true };

    case 'attachment_upload_failed':
      return { label: t('support:event.attachment_upload_failed'), isKnown: true };

    case 'notification_sent':
      return { label: t('support:notifications.sent'), isKnown: true };

    case 'notification_skipped':
      return { label: t('support:notifications.skipped'), isKnown: true };

    case 'notification_failed':
      return { label: t('support:notifications.failed'), isKnown: true };

    case 'ticket.updated': {
      if (Object.prototype.hasOwnProperty.call(payload, 'priority') && payload.priority != null) {
        const to = translatePriority(t, payload.priority);
        return {
          label: t('support:event.priority_changed_to', { to }),
          isKnown: true,
        };
      }
      if (payload.action === 'add_watcher') {
        return { label: t('support:event.watcher_added'), isKnown: true };
      }
      if (payload.action === 'remove_watcher') {
        return { label: t('support:event.watcher_removed'), isKnown: true };
      }
      if (payload.action === 'mute_watcher') {
        return { label: t('support:event.watcher_muted'), isKnown: true };
      }
      if (payload.action === 'recalculate_sla') {
        return { label: t('support:event.sla_recalculated'), isKnown: true };
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'deadline')) {
        return { label: t('support:event.deadline_updated'), isKnown: true };
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'awaiting_user_id')) {
        return { label: t('support:event.awaiting_updated'), isKnown: true };
      }
      return { label: t('support:event.ticket_updated'), isKnown: true };
    }

    default:
      return { label: t('support:event.generic'), isKnown: false };
  }
}

export const SUPPORT_EVENT_TYPE_CATALOG = [
  'ticket.created',
  'ticket.updated',
  'ticket.status_changed',
  'ticket.priority_changed',
  'ticket.assigned',
  'ticket.unassigned',
  'ticket.message_added',
  'ticket.internal_note_added',
  'attachment_uploaded',
  'attachment_upload_failed',
  'attachment_deleted',
  'notification_sent',
  'notification_skipped',
  'notification_failed',
];
